/** Kleinteile des Kapitels „Hebel" (P4). Sektionsweite Bausteine kommen aus `components/auswertungen/kennzahlTeile`. */
import { FC, ReactNode } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { int, jeTausend } from '../../../components/auswertungen/kennzahlTeile';
import { KANAL_FENSTER, KanalFensterMonate } from '../../../helpers/auswertungenData';
import { formatNumber } from '../../../helpers/numberUtils';

/**
 * Anzahl, darunter klein die Quote je 1.000. `grau`: zu wenige Fälle (Grund im Tooltip). `null` = nicht messbar („—",
 * Grund im Tooltip) — nie als 0. `stellen`: feste Nachkommastellen der Quote für die ganze Spalte (P5-Review #30, aus
 * `festeStellen`); ohne Angabe zwei geltende Stellen je Zelle wie bisher.
 */
export const ZahlMitQuote: FC<{
  n: number | null;
  je?: number | null;
  einheit?: string;
  grau?: string | null;
  fehltGrund?: string;
  stellen?: number;
}> = ({ n, je, einheit = 'je 1.000', grau, fehltGrund, stellen }) => {
  if (n === null) {
    return (
      <Tooltip title={fehltGrund ?? 'nicht messbar'} arrow placement="top">
        <Box component="span" sx={{ cursor: 'help', color: 'text.secondary' }}>
          —
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {int(n)}
      {je !== undefined && (
        <Tooltip title={grau ?? ''} disableHoverListener={!grau} arrow placement="top">
          <Box
            component="span"
            sx={{ display: 'block', fontSize: 11, color: grau ? 'text.disabled' : 'text.secondary', whiteSpace: 'nowrap' }}
          >
            {stellen === undefined || je === null || je === undefined ? jeTausend(je) : formatNumber(je, stellen)} {einheit}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

/** Zeitraum-Wahl einer Karte (3 / 6 / 12 Monate) — gilt nur für diese Karte. */
export const KartenFenster: FC<{ monate: KanalFensterMonate; onChange: (m: KanalFensterMonate) => void }> = ({ monate, onChange }) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={monate}
    onChange={(_e, v: KanalFensterMonate | null) => v && onChange(v)}
    aria-label="Zeitraum der Karte"
  >
    {KANAL_FENSTER.map((m) => (
      <ToggleButton key={m} value={m} sx={{ px: 1.25, textTransform: 'none', whiteSpace: 'nowrap' }}>
        {m} Monate
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

/** Kleine graue Zeile unter einem Namen. */
export const Unterzeile: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, whiteSpace: 'normal' }}>
    {children}
  </Box>
);

/** `2026-10-25 06:00` → „Sa 25.10. 06:00". */
const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
export function versandZeit(v: string): string {
  const [j, m, t] = v.slice(0, 10).split('-').map(Number);
  const w = new Date(Date.UTC(j, m - 1, t)).getUTCDay();
  return `${WOCHENTAG[w]} ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}. ${v.slice(11, 16)}`;
}

/** `2026-10-27` → „27.10.2026". */
export const tag = (v: string): string => `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}`;
